import { forwardRef, RefObject, useEffect } from 'react';

import {
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  Spinner,
  Stack,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  DropdownInput,
  DropdownOption,
  CrossOutline,
  DatePickerInput,
  ArrowRightOutline,
  ArrowLeftOutline,
} from '@aslanpay/portal-common-lib';

import useFetchPayCalendarsForEmployee from 'app/hooks/useFetchPayCalendarsForEmployee';

import { useCreateEmployeeStore } from 'app/store';

import { employeePayCalendarFormSchema } from 'app/utils/schemas';

import { useParams } from 'react-router-dom';
import useAuthContext from 'app/hooks/useAuthContext';

import {
  EmployeePayCalendarFormFields,
  EmployeeFormProps,
  EmployeeCompanyFormFields,
} from '../types';

const EmployeePayCalendarForm = forwardRef<HTMLElement, EmployeeFormProps>(
  ({ id, onStepAction, previousStep, canPrevStep }, ref) => {
    const employeeData = useCreateEmployeeStore((state) => state.employeeData);
    const setEmployeeData = useCreateEmployeeStore((state) => state.setEmployeeData);

    const { companyId: urlCompanyId } = useParams();
    const { user } = useAuthContext();
    const { companyId: storeCompanyId } = employeeData as EmployeeCompanyFormFields;

    // Use companyId from store, URL params, or user context as fallback
    const companyId = storeCompanyId || urlCompanyId || user?.companyId;

    const {
      register,
      control,
      handleSubmit,
      watch,
      resetField,
      setFocus,
      getValues,
      setValue,
      formState: { errors, isValid },
    } = useForm<EmployeePayCalendarFormFields>({
      mode: 'onBlur',
      defaultValues: employeeData as EmployeePayCalendarFormFields,
      resolver: yupResolver(employeePayCalendarFormSchema),
    });

    const watchExternalPayrollId = watch('externalPayrollId');

    const handleNextStep = (data: EmployeePayCalendarFormFields) => {
      setEmployeeData(data);
      onStepAction();
    };

    const { data: payCalendars, isLoading: isLoadingPayCalendars } =
      useFetchPayCalendarsForEmployee([companyId || '']);

    const payCalendarOptions: DropdownOption[] =
      payCalendars?.list.map((payCalendar) => ({
        value: payCalendar.id,
        text: payCalendar.name,
      })) || [];

    // Pre-select the only available pay calendar
    useEffect(() => {
      if (payCalendars?.list.length === 1) {
        setValue('payCalendarId', payCalendars.list[0].id, { shouldValidate: true });
      }
    }, [payCalendars?.list, setValue]);

    const resetAndFocusField = (field: keyof EmployeePayCalendarFormFields) => {
      resetField(field, { defaultValue: '' });
      setFocus(field);
    };

    const handleBackCLick = () => {
      const formValues = getValues();
      setEmployeeData(formValues);
      previousStep();
    };

    return isLoadingPayCalendars ? (
      <Center width="full" height="full" flexGrow={1}>
        <Spinner color="brand.purple" emptyColor="brand.purpleAlpha30" size="xl" />
      </Center>
    ) : (
      <form id={id} onSubmit={handleSubmit(handleNextStep)} noValidate>
        <Stack spacing="20px">
          <FormControl isInvalid={!!errors?.payCalendarId?.message} isRequired>
            <FormLabel>Pay calendar</FormLabel>
            <Controller
              control={control}
              name="payCalendarId"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="Select a pay calendar"
                  defaultValue={payCalendarOptions.find(
                    (payCalendar) => payCalendar.value === value
                  )}
                  options={payCalendarOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.payCalendarId?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.startDate?.message}>
            <FormLabel>Start date</FormLabel>
            <Controller
              control={control}
              name="startDate"
              render={({ field: { onChange, onBlur, value } }) => (
                <DatePickerInput
                  placeholder="When will the employee start to accrue balance?"
                  defaultValue={value}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.startDate?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.externalPayrollId?.message} isRequired>
            <FormLabel>External payroll ID</FormLabel>
            <InputGroup>
              <Input
                type="text"
                placeholder="Enter employee existing payroll ID"
                {...register('externalPayrollId')}
              />
              {watchExternalPayrollId && (
                <InputRightElement>
                  <IconButton
                    aria-label="Clear external payroll id"
                    icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                    variant="link"
                    onClick={() => resetAndFocusField('externalPayrollId')}
                  />
                </InputRightElement>
              )}
            </InputGroup>
            <FormErrorMessage>{errors?.externalPayrollId?.message}</FormErrorMessage>
          </FormControl>

          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canPrevStep}
              leftIcon={<ArrowLeftOutline boxSize="20px" />}
              onClick={handleBackCLick}
              mr="16px"
            >
              Back
            </Button>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid}
              rightIcon={<ArrowRightOutline boxSize="20px" />}
            >
              Next
            </Button>
          </Portal>
        </Stack>
      </form>
    );
  }
);

EmployeePayCalendarForm.displayName = 'EmployeePayCalendarForm';

export default EmployeePayCalendarForm;
